import React, {useEffect, useState} from 'react';
import _ from 'lodash';
import './index.scss';
import { Link, useLocation } from 'react-router-dom';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { BsChevronDown as ChevronDownIcon } from 'react-icons/bs';
import Dropdown from 'react-bootstrap/Dropdown';
import Button from 'react-bootstrap/Button';
import InputGroup from 'react-bootstrap/InputGroup';
import Form from 'react-bootstrap/Form';
import { translations } from '@janus.team/janus-particles';
import qs from 'qs';
import { useCurrentAccount } from 'account-selector';
import { useCurrentDataSource } from 'webhooks/data-source-selector';
import * as config from 'config/index.js';
import { useWebhooksForAccountQuery } from 'api/watchman';

const SLAs = {
  low: '<5hr',
  normal: '<4hr',
  high: '<3hr',
  urgent: '<2hr',
  emergency: '<1hr'
};

const mergeSLAs = (severities) => _.map(severities, (severity) => ({...severity, sla: SLAs[severity.severity]}));

const useSeverities = () => {
  const dataSourceType = useCurrentDataSource()?.type;
  const { tenantId } = useCurrentAccount() || {};
  const webhooks = useWebhooksForAccountQuery(tenantId)?.data;
  const severities = _.filter(webhooks, { type: dataSourceType });
  return mergeSLAs(severities) || [];
};

const useCurrentSeverity = () => {
  const severities = useSeverities();
  const parsedQuery = qs.parse(useLocation()?.search || '', { ignoreQueryPrefix: true }) || {};
  const { severity } = parsedQuery || {};
  return _.find(severities, { severity });
};

export const SeveritySelector = () => {
  const severities = useSeverities();
  const t = translations.useT();
  const selectedSeverity = useCurrentSeverity();
  const { pathname } = useLocation();
  const search = qs.parse(useLocation()?.search || '', { ignoreQueryPrefix: true });
  const { account, dataSource } = useAccountAndDataSourceData();
  const disabled = !_.every([account?.id, dataSource?.type]);
  const label = _.get(_.find([
    [!account?.id, t('First Select an Account')],
    [!dataSource?.type, t('First Select a Monitoring Service')],
    [true, t('Choose Severity')]
  ], 0), 1);
  return (
    <div className="account-select container-xxl container-fluid">  
      <h2 className="my-6">{t('Severity')}</h2>
      <p>{t('Select the appropriate priority for the incident. Please choose wisely: Tagging all incidents as emergencies is not reccommended and may delay response times for actual emergencies.')}</p>
      <Dropdown className="mb-10">
        <Dropdown.Toggle as={Button} variant="outline border" className="w-100" disabled={disabled}>
          <div className="d-flex align-items-center justify-content-between">
            {selectedSeverity ? (
              <SeverityItem severity={selectedSeverity} style={{maxWidth: '75%'}}/>
            ) : (
              label
            )}
          <ChevronDownIcon className="text-gray700" style={{width: '1.5rem'}}/>
          </div>
        </Dropdown.Toggle>
        <Dropdown.Menu className="w-100 shadow">
          {_.map(severities, (severity, idx) => {
            const key = `${severity?.severity}${idx}`;
            const toSearch = qs.stringify(_.merge({}, search, { severity: severity?.severity }));
            return (
              <Dropdown.Item as={Link} to={{ pathname, search: toSearch }} key={key}>
                <SeverityItem severity={severity}/>
              </Dropdown.Item>
            );
          })}
        </Dropdown.Menu>
      </Dropdown>
      <WebhookUrl />
    </div>
  );
};

const SeverityItem = ({severity, ...props}) => {
  const t = translations.useT();
  return (
    <div className="text-start" {...props}>
      <div>
        {_.capitalize(t(severity?.severity))} ( {t(severity?.sla)} )
      </div>
      <small className="d-block text-muted text-truncate" >
        {t(severity?.name)}
      </small>
    </div>
  );
};

const useAccountAndDataSourceData = () => {
  const account = useCurrentAccount();
  const dataSource = useCurrentDataSource();
  return { account, dataSource };
};

const WebhookUrl = () => {
  const t = translations.useT();
  const selectedSeverity = useCurrentSeverity();

  const url = config.isProd() ?
  `https://watchman.api.manage.rackspace.com${selectedSeverity?.href}`:
  `https://watchman.api.dev.manage.rackspace.com${selectedSeverity?.href}`;

  return (
    (selectedSeverity) ? (
      <Form.Group>
        <Form.Label htmlFor="webhook-url">Webhook URL</Form.Label>
        <InputGroup>
          <Form.Control aria-label={t('Webhook URL')} readOnly value={url} id="webhook-url"/>
          <CopyUrl className="ms-4" url={url} />
        </InputGroup>
      </Form.Group>
    ) : null
  );
};

const CopyUrl = ({ url, ...props }) => {
  const t = translations.useT();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const timeoutId = copied ? global.setTimeout(() => {
      setCopied(false);
    }, config.copiedLabelDelay()) : null;
    return () => global.clearTimeout(timeoutId);
  }, [copied, setCopied]);

  return (
    <CopyToClipboard className="m-0" text={url} onCopy={() => setCopied((copied) => (!copied))}>
      <Button {...props}>
        {!copied && (<span>{t('Copy')}</span>)}
        {copied && (<span>{t('Copied!')}</span>)}
      </Button>
    </CopyToClipboard>
  );
};

export default SeveritySelector;
